import { Box, Image, SkeletonLoader, Typography } from "@drone-js/core";
import type { UnifiedPropertyCardItem } from "apps/oneOnboarding/complete/useCompletePagePropertyViewModel";
import { useStaticDataAtomValue } from "apps/oneOnboarding/staticDataAtom";
import type { FunctionComponent } from "react";
import { BatchPropertyBadge } from "./BatchPropertyBadge";

type BatchPropertyContentProps = UnifiedPropertyCardItem & {
    statusText?: string;
    statusColor?: string;
};

export const BatchPropertyContent: FunctionComponent<BatchPropertyContentProps> = (props) => {
    const { name, imageUrl, id, location, isPublishing } = props;

    const {
        cmsData: {
            complete: { unnamedPropertyText },
        },
    } = useStaticDataAtomValue();

    const badge = <BatchPropertyBadge publishing={isPublishing} />;
    const displayName = name || unnamedPropertyText;

    return (
        <Box
            display="flex"
            alignItems="center"
            data-element-name="batch-property-content"
            data-testid={`batch-property-content-${id}`}
        >
            <Box
                flexShrink={0}
                me="16"
                width="48px"
                height="48px"
                borderRadius="8"
                overflow="hidden"
                position="relative"
                data-testid={`batch-property-image-${id}`}
            >
                <SkeletonLoader label={displayName} shape="rectangle" width="64px" height="64px" />
                <Box position="absolute" top={0}>
                    <Image
                        src={imageUrl}
                        width="64px"
                        height="64px"
                        loading="lazy-blur"
                        borderRadius="8"
                        alt={displayName}
                    />
                </Box>
            </Box>
            <Box width="100%" wordBreak="break-all">
                <Typography
                    variant="body-default-strong"
                    textColor="base.default"
                    lineClamp={2}
                    data-testid={`batch-property-name-${id}`}
                >
                    {displayName}
                </Typography>
                <Box
                    pt="4"
                    gap="8"
                    {...(isPublishing
                        ? { display: "block", qM: { display: "flex", alignItems: "center" } }
                        : { display: "flex", alignItems: "center" })}
                >
                    {badge}
                    <Typography
                        variant="caption-default"
                        textColor="base.subtle"
                        lineClamp={1}
                        data-testid={`batch-property-info-${id}`}
                    >
                        {id} · {location}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};
